<template>
  <div class="banner grid video">
    <button
      v-show="!isYoutube && !commentMode"
      class="btn-float btn-volume"
      @click="handleVideoVolume()"
      :style="muteButtonStyle"
    >
      <i class="mdi" :class="volumeIcon"></i>
    </button>
    <template>
      <video
        id="videoJsContainer"
        :style="videoJsContainerStyle"
        class="cloudFlareVideoContainer"
        preload="auto"
      >
        <source :src="cloudflareVideoSrc" type="application/x-mpegURL" />
      </video>
      <div class="video-wrapper" :style="videoContainerStyle">
        <div id="video-container"></div>
      </div>
    </template>

    <div class="video-actions text-center grid-bottom">
      <button
        class="btn btn-stretch btn-extra-round text-bold"
        :class="{ hidden: areActionsHidden }"
        :style="buttonStyle(firstOption.estilo)"
        @click="$emit('next', firstOption.id)"
      >
        {{ firstOption.nome }}
      </button>
    </div>
  </div>
</template>

<script>
import buttonStyle from "../../../../interactions/components/interaction/mixins/button-style";
import dataInteractionProp from "../mixins/data-interaction-prop";
import videoCampaign from "../../../../../packages/mixins/video-campaign";
import videojs from "video.js";

export default {
  data() {
    return {
      player: undefined,
      noVolume: false,
    };
  },

  props: {
    areActionsHidden: {
      type: Boolean,
      default: true,
    },
    background: {
      type: String,
      required: false,
    },
    buttonPositioning: {
      type: Number,
      required: true,
    },
    commentMode: {
      type: Boolean,
      required: false,
    },
    isDesktop: {
      required: false,
      default: false,
    },
  },

  watch: {
    async background(newVal, oldVal) {
      if (!this.isCloudflareVideo) {
        if (oldVal && this.checkIfInteractionIsYoutubeVideo(oldVal)) {
          await this.player.destroy();
          this.noVolume = false;
          this.initializeVideo();
        }
      }
    },
    async cloudflareVideoSrc(newVal, oldVal) {
      this.noVolume = false;
      if (oldVal && this.checkIfInteractionIsYoutubeVideo()) {
        this.initializeVideo();
      } else if (newVal) {
        this.initializeVideo();
      }
    },
    async isYoutube(newVal, oldVal) {
      if (oldVal && this.player) await this.player.destroy();
    },
  },

  computed: {
    options() {
      return this.interaction.opcoes;
    },

    firstOption() {
      return this.options[0] || null;
    },

    muteButtonStyle() {
      return {
        right: `${this.buttonPositioning + 55}px`,
      };
    },

    volumeIcon() {
      return this.noVolume ? "mdi-volume-variant-off" : "mdi-volume-medium";
    },

    interactionStored() {
      return this.$store.state.interactions.list.find(
        (interaction) => interaction.id === this.interaction.id
      );
    },

    videoJsContainerStyle() {
      return {
        height: this.isDesktop ? "100vh" : "560px",
      };
    },

    videoContainerStyle() {
      let height;
      if (this.isCloudflareVideo) {
        height = "0px";
      } else {
        height = this.isDesktop ? "100vh" : "560px";
      }
      return {
        height,
      };
    },

    cloudflareVideoSrc() {
      if (this.interaction.videoMobile) {
        return this.interaction.videoMobile.videoUrl.replace(
          "watch",
          "manifest/video.m3u8"
        );
      }
      return undefined;
    },
  },

  methods: {
    initializeVideo() {
      this.noVolume = true;
      
      if (this.isYoutube) {
        this.hideCloudflarePlayer();
        this.setYoutubeApiScript();
      } else {
        this.showCloudflarePlayer();
        this.setVideoJsConfig();
        this.player.play();
        this.player.muted(true);
        this.$emit("on-countdown-init");
      }
    },

    async onIframeAPIReady() {
      const config = {
        height: this.isDesktop ? "100%" : "560px",
        width: "100%",
        videoId: this.getVideoId(this.background),
        playerVars: {
          enablejsapi: 1, // Desativa a API JavaScript do player
          controls: 0, // Desativa os controles padrão do YouTube
          fs: 1, // Permite o modo de tela cheia
          modestbranding: 1, // Remove a marca do YouTube
          iv_load_policy: 3, // Desativa a exibição de anotações
          playsinline: 1, // Reproduz em linha em dispositivos móveis
          autoplay: 1, // Inicia automaticamente o vídeo
          showinfo: 0,
          rel: 0,
        },
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
        },
      };

      this.player = new window.YT.Player("video-container", config);
    },

    onPlayerReady(event) {
      event.target.playVideo();
      event.target.mute()
      this.$emit("on-countdown-init");
    },

    onPlayerStateChange(event) {
      if (event.data == window.YT.PlayerState.ENDED) {
        this.player.playVideo();
      }
    },

    stopVideo() {
      if (this.countdown) return;
      if (!this.isPaused) {
        this.isPaused = true;
        this.isYoutube ? this.player.pauseVideo() : this.player.pause();
      } else {
        this.isPaused = false;
        this.isYoutube ? this.player.playVideo() : this.player.play();
      }
    },

    checkApiDefinition() {
      if (
        typeof YT === "undefined" ||
        typeof window.YT.Player === "undefined"
      ) {
        setTimeout(this.checkApiDefinition, 100);
      } else {
        this.onIframeAPIReady();
      }
    },

    setVideoJsConfig() {
      const vid = document.getElementById("videoJsContainer", {
        controls: false,
        fluid: true,
      });
      this.player = videojs(vid);
      this.player.src({ src: this.cloudflareVideoSrc });
    },

    setYoutubeApiScript() {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      window.onYouTubeIframeAPIReady = this.checkApiDefinition();
    },

    handleVideoVolume() {
      this.noVolume = !this.noVolume;
      if (this.isYoutube) {
        this.noVolume ? this.player.mute() : this.player.unMute();
      } else {
        this.noVolume ? this.player.muted(true) : this.player.muted(false);
      }
    },

    checkIfInteractionIsYoutubeVideo(url = this.interactionStored) {
      const youtubePattern = /youtube\.com|youtu\.be/;

      if (typeof url === "string") {
        return youtubePattern.test(url);
      }

      const mediaMobile = url.mediaMobile || this.interactionStored.mediaMobile;

      return youtubePattern.test(mediaMobile);
    },

    removeScripts() {
      const scriptElements = document.querySelectorAll("script");

      scriptElements.forEach(function (script) {
        const srcAttribute = script.getAttribute("src");

        if (
          srcAttribute &&
          srcAttribute === "https://www.youtube.com/iframe_api"
        ) {
          script.parentNode.removeChild(script);
        }
      });
    },

    hideCloudflarePlayer() {
      const cloudflarePlayer = document.getElementById("videoJsContainer");
      cloudflarePlayer.style.display = "none";
    },

    showCloudflarePlayer() {
      const cloudflarePlayer = document.getElementById("videoJsContainer");
      cloudflarePlayer.style.display = "block";
    },
  },

  mounted() {
    this.initializeVideo();
  },

  beforeDestroy() {
    this.removeScripts();
  },

  mixins: [buttonStyle, dataInteractionProp, videoCampaign],
};
</script>

<style lang="scss" scoped>
.video {
  position: absolute;
  top: 0;
  width: 100%;

  .btn-volume {
    top: 10px;
  }

  #videoJsContainer {
    width: 100%;
    height: 560px;
    video {
      width: 100%;
      height: 100%;
    }

    ::v-deep .vjs-control-bar {
      display: none;
    }
    ::v-deep .vjs-loading-spinner {
      display: none;
    }
    ::v-deep .vjs-big-play-button {
      display: none;
    }
    ::v-deep .vjs-error-display {
      display: none;
    }
    ::v-deep .vjs-text-track-settings {
      display: none;
    }
  }

  .video-wrapper {
    pointer-events: none;
  }

  .video-actions {
    z-index: 9999;
  }
}
.btn {
  padding-left: 0;
  padding-right: 0;
}
.hidden {
  display: none;
}
</style>
